<template>
  <a-modal
    :open="visible"
    :confirm-loading="loading"
    :footer="null"
    width="800px"
    @cancel="handleCancel"
  >
    <template #title>
      <div class="modal-title">
        <i class="fa-solid fa-box" />
        {{ isEdit ? "상품 수정" : "상품 추가" }}
      </div>
    </template>

    <div class="modal-content">
      <a-form layout="vertical">
        <!-- 기본 정보 -->
        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item label="상품명" required>
              <a-input
                v-model:value="form.name"
                placeholder="상품명을 입력하세요"
              />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="과금 방식" required>
              <a-select
                v-model:value="form.billingType"
                placeholder="과금 방식 선택"
                @change="calculatePricing"
              >
                <a-select-option value="monthly">월간</a-select-option>
                <a-select-option value="yearly">연간</a-select-option>
                <a-select-option value="oneTime">일회성</a-select-option>
                <a-select-option value="perTransaction">건당</a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
        </a-row>

        <!-- 상품 타입 / 리셀러 또는 병원/제조사 -->
        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item label="상품 타입" required>
              <a-select
                v-model:value="form.type"
                placeholder="상품 타입 선택"
                @change="onTypeChange"
              >
                <a-select-option
                  v-for="(label, key) in SubscriptionPackageTypeLabel"
                  :key="key"
                  :value="key"
                >
                  {{ label }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <!-- 리셀러 타입일 때 -->
            <a-form-item
              v-if="form.type === SubscriptionPackageType.RESELLER"
              label="리셀러"
              required
            >
              <a-select
                v-model:value="form.resellerId"
                placeholder="리셀러 선택"
                allow-clear
              >
                <a-select-option
                  v-for="reseller in resellers"
                  :key="reseller._id"
                  :value="reseller._id"
                >
                  {{ reseller.name }}
                </a-select-option>
              </a-select>
            </a-form-item>
            <!-- 커스텀 타입일 때 -->
            <a-form-item
              v-else-if="form.type === SubscriptionPackageType.CUSTOM"
              label="병원 / 제조사"
              required
            >
              <a-select
                v-model:value="form.resellerId"
                placeholder="병원명 또는 사업자등록번호로 검색"
                allow-clear
                show-search
                :filter-option="filterCustomCompany"
              >
                <a-select-option
                  v-for="company in customCompanies"
                  :key="company._id"
                  :value="company._id"
                  :label="company.name"
                  :data-cno="company.cNo"
                >
                  <span
                    class="company-type-tag"
                    :class="company.type.toLowerCase()"
                  >
                    {{ company.type === "HOSPITAL" ? "병원" : "제조사" }}
                  </span>
                  {{ company.name }}
                  <span v-if="company.cNo" class="company-cno"
                    >({{ formatBusinessNo(company.cNo) }})</span
                  >
                </a-select-option>
              </a-select>
            </a-form-item>
            <!-- 기본 타입일 때 빈 공간 -->
            <a-form-item v-else label=" " :colon="false">
              <span class="type-hint"
                >기본 상품은 특정 업체에 귀속되지 않습니다.</span
              >
            </a-form-item>
          </a-col>
        </a-row>

        <!-- 제품 구성 -->
        <a-divider>제품 구성</a-divider>
        <div class="product-items">
          <a-checkbox-group
            v-model:value="form.items"
            @change="calculatePricing"
          >
            <a-row :gutter="16">
              <a-col
                :span="6"
                v-for="product in systemProducts"
                :key="product.type"
              >
                <a-checkbox :value="product.type" class="product-checkbox">
                  <div class="product-item">
                    <i :class="['fa-solid', getProductIcon(product.type)]" />
                    <span>{{ product.name }}</span>
                  </div>
                </a-checkbox>
              </a-col>
            </a-row>
          </a-checkbox-group>
        </div>

        <!-- 가격 정보 -->
        <a-divider>가격 정보</a-divider>
        <div class="pricing-section">
          <a-row :gutter="24">
            <!-- 설치비 -->
            <a-col :span="12">
              <div class="price-card">
                <div class="price-card-title">설치비</div>
                <div class="price-row">
                  <span class="price-label">기준가</span>
                  <span class="price-value">{{
                    formatCurrency(pricing.baseInstallationFee)
                  }}</span>
                </div>
                <div class="discount-input">
                  <a-radio-group
                    v-model:value="form.installationDiscountType"
                    @change="onInstallationDiscountTypeChange"
                    class="discount-radio-group"
                  >
                    <a-radio value="amount">할인액</a-radio>
                    <a-radio value="rate">할인율</a-radio>
                  </a-radio-group>
                  <a-input-number
                    v-if="form.installationDiscountType === 'rate'"
                    :key="'install-rate'"
                    v-model:value="form.installationDiscountValue"
                    :min="0"
                    :max="100"
                    :formatter="(v) => `${v}%`"
                    :parser="(v) => v.replace('%', '')"
                    class="discount-value-input"
                    @change="calculatePricing"
                  />
                  <a-input-number
                    v-else
                    :key="'install-amount'"
                    v-model:value="form.installationDiscountValue"
                    :min="0"
                    :max="pricing.baseInstallationFee"
                    :formatter="
                      (v) => `₩ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                    "
                    :parser="(v) => v.replace(/₩\s?|(,*)/g, '')"
                    class="discount-value-input"
                    @change="calculatePricing"
                  />
                </div>
                <div class="price-row final">
                  <span class="price-label">최종가</span>
                  <span class="price-value highlight">{{
                    formatCurrency(pricing.finalInstallationFee)
                  }}</span>
                </div>
                <div
                  v-if="pricing.installationDiscountRate > 0"
                  class="discount-info"
                >
                  {{ pricing.installationDiscountRate }}% 할인 적용
                </div>
              </div>
            </a-col>

            <!-- 사용료 -->
            <a-col :span="12">
              <div class="price-card">
                <div class="price-card-title">
                  사용료 ({{ getBillingTypeLabel(form.billingType) }})
                </div>
                <div class="price-row">
                  <span class="price-label">기준가</span>
                  <span class="price-value">{{
                    formatCurrency(pricing.baseUsageFee)
                  }}</span>
                </div>
                <div class="discount-input">
                  <a-radio-group
                    v-model:value="form.usageDiscountType"
                    @change="onUsageDiscountTypeChange"
                    class="discount-radio-group"
                  >
                    <a-radio value="amount">할인액</a-radio>
                    <a-radio value="rate">할인율</a-radio>
                  </a-radio-group>
                  <a-input-number
                    v-if="form.usageDiscountType === 'rate'"
                    :key="'usage-rate'"
                    v-model:value="form.usageDiscountValue"
                    :min="0"
                    :max="100"
                    :formatter="(v) => `${v}%`"
                    :parser="(v) => v.replace('%', '')"
                    class="discount-value-input"
                    @change="calculatePricing"
                  />
                  <a-input-number
                    v-else
                    :key="'usage-amount'"
                    v-model:value="form.usageDiscountValue"
                    :min="0"
                    :max="pricing.baseUsageFee"
                    :formatter="
                      (v) => `₩ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                    "
                    :parser="(v) => v.replace(/₩\s?|(,*)/g, '')"
                    class="discount-value-input"
                    @change="calculatePricing"
                  />
                </div>
                <div class="price-row final">
                  <span class="price-label">최종가</span>
                  <span class="price-value highlight">{{
                    formatCurrency(pricing.finalUsageFee)
                  }}</span>
                </div>
                <div v-if="pricing.usageDiscountRate > 0" class="discount-info">
                  {{ pricing.usageDiscountRate }}% 할인 적용
                </div>
              </div>
            </a-col>
          </a-row>
        </div>

        <!-- 추가 설정 -->
        <a-divider>추가 설정</a-divider>
        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item label="설명">
              <a-textarea
                v-model:value="form.description"
                :rows="2"
                placeholder="상품에 대한 설명을 입력하세요"
              />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="상태">
              <a-switch
                v-model:checked="form.isActive"
                checked-children="활성"
                un-checked-children="비활성"
              />
            </a-form-item>
          </a-col>
        </a-row>
      </a-form>

      <div class="modal-footer">
        <a-button @click="handleCancel">취소</a-button>
        <a-button
          type="primary"
          :loading="loading"
          @click="handleSubmit"
          class="yellow-button"
        >
          {{ isEdit ? "수정" : "저장" }}
        </a-button>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { message } from "ant-design-vue";
import { subscriptionPackageApi } from "../api";
import {
  BillingTypeLabel,
  SystemProductType,
  SystemProductTypeIcon,
  SubscriptionPackageType,
  SubscriptionPackageTypeLabel,
} from "../enums";

export default {
  name: "UpsertPackageModal",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    packageData: {
      type: Object,
      default: null,
    },
    systemProducts: {
      type: Array,
      default: () => [],
    },
    resellers: {
      type: Array,
      default: () => [],
    },
    customCompanies: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["close", "saved"],
  data() {
    return {
      loading: false,
      form: {
        name: "",
        type: SubscriptionPackageType.GENERAL,
        resellerId: null,
        billingType: "monthly",
        items: [], // 선택된 제품 타입 배열 (예: ['chatbot', 'voicebot'])
        installationDiscountType: "amount",
        installationDiscountValue: 0,
        usageDiscountType: "amount",
        usageDiscountValue: 0,
        description: "",
        isActive: true,
      },
      SubscriptionPackageType,
      SubscriptionPackageTypeLabel,
      pricing: {
        baseInstallationFee: 0,
        baseUsageFee: 0,
        finalInstallationFee: 0,
        finalUsageFee: 0,
        installationDiscountRate: 0,
        usageDiscountRate: 0,
      },
    };
  },
  computed: {
    isEdit() {
      return !!this.packageData;
    },
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.initForm();
      }
    },
    packageData: {
      handler() {
        if (this.visible) {
          this.initForm();
        }
      },
      immediate: true,
    },
  },
  methods: {
    initForm() {
      if (this.packageData) {
        // 수정 모드
        this.form.name = this.packageData.name;
        this.form.type =
          this.packageData.type || SubscriptionPackageType.GENERAL;
        this.form.resellerId = this.packageData.resellerId || null;
        this.form.billingType = this.packageData.billingType;
        this.form.description = this.packageData.description || "";
        this.form.isActive = this.packageData.isActive;

        // items는 이제 productType 배열로 직접 들어옴
        this.form.items = [...(this.packageData.items || [])];

        // 할인 정보는 discounts 배열에서 가져옴
        const discounts = this.packageData.discounts || [];
        const installationDiscount = discounts.find(
          (d) => d.target === "installation"
        );
        const usageDiscount = discounts.find((d) => d.target === "usage");

        // 설치비 할인 정보
        this.form.installationDiscountType =
          installationDiscount?.discountType || "amount";
        this.form.installationDiscountValue = installationDiscount?.value || 0;

        // 사용료 할인 정보
        this.form.usageDiscountType = usageDiscount?.discountType || "amount";
        this.form.usageDiscountValue = usageDiscount?.value || 0;
      } else {
        // 생성 모드
        this.form = {
          name: "",
          type: SubscriptionPackageType.GENERAL,
          resellerId: null,
          billingType: "monthly",
          items: [],
          installationDiscountType: "amount",
          installationDiscountValue: 0,
          usageDiscountType: "amount",
          usageDiscountValue: 0,
          description: "",
          isActive: true,
        };
      }

      this.calculatePricing();
    },
    calculateBasePrice() {
      // 시스템 제품 기준으로 기준가 계산
      let baseInstallationFee = 0;
      let baseUsageFee = 0;

      const billingTypeKey = {
        monthly: "monthly",
        yearly: "yearly",
        oneTime: "oneTime",
        perTransaction: "perTransaction",
      };

      for (const product of this.systemProducts) {
        if (this.form.items.includes(product.type)) {
          baseInstallationFee += product.installationFee || 0;
          baseUsageFee +=
            product.usageFee?.[billingTypeKey[this.form.billingType]] || 0;
        }
      }

      return { baseInstallationFee, baseUsageFee };
    },
    calculatePricing() {
      // 기준 가격 계산
      let baseInstallationFee = 0;
      let baseUsageFee = 0;

      const billingTypeKey = {
        monthly: "monthly",
        yearly: "yearly",
        oneTime: "oneTime",
        perTransaction: "perTransaction",
      };

      for (const product of this.systemProducts) {
        // items 배열에 해당 제품 타입이 포함되어 있으면 가격 추가
        if (this.form.items.includes(product.type)) {
          baseInstallationFee += product.installationFee || 0;
          baseUsageFee +=
            product.usageFee?.[billingTypeKey[this.form.billingType]] || 0;
        }
      }

      // 할인 계산
      let installationDiscount = 0;
      let usageDiscount = 0;

      if (this.form.installationDiscountType === "amount") {
        installationDiscount = this.form.installationDiscountValue || 0;
      } else {
        installationDiscount =
          baseInstallationFee *
          ((this.form.installationDiscountValue || 0) / 100);
      }

      if (this.form.usageDiscountType === "amount") {
        usageDiscount = this.form.usageDiscountValue || 0;
      } else {
        usageDiscount =
          baseUsageFee * ((this.form.usageDiscountValue || 0) / 100);
      }

      const finalInstallationFee = Math.max(
        0,
        baseInstallationFee - installationDiscount
      );
      const finalUsageFee = Math.max(0, baseUsageFee - usageDiscount);

      // 할인율 계산
      const installationDiscountRate =
        baseInstallationFee > 0
          ? Math.round(
              (installationDiscount / baseInstallationFee) * 100 * 100
            ) / 100
          : 0;
      const usageDiscountRate =
        baseUsageFee > 0
          ? Math.round((usageDiscount / baseUsageFee) * 100 * 100) / 100
          : 0;

      this.pricing = {
        baseInstallationFee,
        baseUsageFee,
        finalInstallationFee,
        finalUsageFee,
        installationDiscountRate,
        usageDiscountRate,
      };
    },
    handleCancel() {
      this.$emit("close");
    },
    async handleSubmit() {
      // 유효성 검사
      if (!this.form.name?.trim()) {
        message.warning("상품명을 입력해주세요.");
        return;
      }

      if (!this.form.billingType) {
        message.warning("과금 방식을 선택해주세요.");
        return;
      }

      if (!this.form.items || this.form.items.length === 0) {
        message.warning("최소 1개 이상의 제품을 선택해주세요.");
        return;
      }

      if (
        this.form.type === SubscriptionPackageType.RESELLER &&
        !this.form.resellerId
      ) {
        message.warning("리셀러 타입 상품은 리셀러를 선택해주세요.");
        return;
      }

      if (
        this.form.type === SubscriptionPackageType.CUSTOM &&
        !this.form.resellerId
      ) {
        message.warning("커스텀 타입 상품은 병원 또는 제조사를 선택해주세요.");
        return;
      }

      this.loading = true;
      try {
        // 할인 정보 구성 (백엔드 DTO 형식에 맞춤)
        const discounts = [];

        // 설치비 할인
        if (this.form.installationDiscountValue > 0) {
          discounts.push({
            target: "installation",
            discountType: this.form.installationDiscountType,
            value: this.form.installationDiscountValue,
          });
        }

        // 사용료 할인
        if (this.form.usageDiscountValue > 0) {
          discounts.push({
            target: "usage",
            discountType: this.form.usageDiscountType,
            value: this.form.usageDiscountValue,
          });
        }

        const data = {
          name: this.form.name,
          type: this.form.type,
          billingType: this.form.billingType,
          items: this.form.items,
          discounts: discounts.length > 0 ? discounts : undefined,
          description: this.form.description,
          isActive: this.form.isActive,
        };

        // 리셀러 또는 커스텀 타입인 경우 resellerId 포함
        if (
          (this.form.type === SubscriptionPackageType.RESELLER ||
            this.form.type === SubscriptionPackageType.CUSTOM) &&
          this.form.resellerId
        ) {
          data.resellerId = this.form.resellerId;
        }

        if (this.isEdit) {
          await subscriptionPackageApi.update(this.packageData.id, data);
          message.success("상품이 수정되었습니다.");
        } else {
          await subscriptionPackageApi.create(data);
          message.success("상품이 생성되었습니다.");
        }

        this.$emit("saved");
        this.$emit("close");
      } catch (error) {
        console.error("상품 저장 실패:", error);
        message.error("상품 저장에 실패했습니다.");
      } finally {
        this.loading = false;
      }
    },
    onInstallationDiscountTypeChange() {
      this.form.installationDiscountValue = 0;
      this.calculatePricing();
    },
    onUsageDiscountTypeChange() {
      this.form.usageDiscountValue = 0;
      this.calculatePricing();
    },
    onTypeChange() {
      // 타입 변경 시 resellerId 초기화 (기본 타입이거나 다른 타입으로 변경 시)
      // 리셀러 ↔ 커스텀 전환 시에도 초기화 (다른 회사 목록을 사용하므로)
      this.form.resellerId = null;
    },
    filterCustomCompany(input, option) {
      // 병원명 또는 사업자등록번호로 검색
      const searchText = input.toLowerCase();
      const name = (option.label || "").toLowerCase();
      const cNo = (option["data-cno"] || "").toLowerCase();
      return name.includes(searchText) || cNo.includes(searchText);
    },
    getProductIcon(type) {
      return SystemProductTypeIcon[type] || "fa-box";
    },
    getBillingTypeLabel(type) {
      return BillingTypeLabel[type] || type;
    },
    formatCurrency(value) {
      if (value === undefined || value === null) return "₩ 0";
      return `₩ ${Number(value).toLocaleString()}`;
    },
    formatBusinessNo(cNo) {
      // 사업자등록번호 포맷: 123-45-67890
      if (!cNo) return "";
      const digits = cNo.replace(/\D/g, "");
      if (digits.length !== 10) return cNo;
      return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.modal-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 600;

  i {
    color: #faad14;
  }
}

.modal-content {
  padding: 24px 24px 16px 24px;
}

.product-items {
  margin-bottom: 16px;
}

.product-item {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 12px;

  .product-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
    font-weight: 600;
    color: #17191c;

    i {
      color: #faad14;
    }
  }
}

.pricing-section {
  margin-bottom: 16px;
}

.price-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;

  .price-card-title {
    font-weight: 600;
    font-size: 14px;
    color: #17191c;
    margin-bottom: 12px;
  }

  .price-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;

    &.final {
      margin-top: 12px;
      padding-top: 12px;
      border-top: 1px solid #e8e8e8;
    }
  }

  .price-label {
    color: #666;
    font-size: 13px;
  }

  .price-value {
    font-weight: 600;
    color: #17191c;

    &.highlight {
      color: #1890ff;
      font-size: 16px;
    }
  }

  .discount-input {
    margin: 8px 0;
  }

  .discount-radio-group {
    display: flex;
    gap: 16px;
    margin-bottom: 8px;

    :deep(.ant-radio-wrapper) {
      font-size: 14px;
      color: #17191c;
    }
  }

  .discount-value-input {
    width: 100%;

    :deep(.ant-input-number-input) {
      font-size: 18px;
      font-weight: 600;
      height: 40px;
      line-height: 40px;
    }
  }

  .discount-info {
    text-align: right;
    font-size: 12px;
    color: #f5222d;
    margin-top: 4px;
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

.yellow-button {
  background-color: #ffe414 !important;
  border-color: #ffe414 !important;
  color: #17191c !important;

  &:hover {
    background-color: #ffd900 !important;
    border-color: #ffd900 !important;
  }
}

.type-hint {
  color: #999;
  font-size: 13px;
}

.company-type-tag {
  display: inline-block;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  margin-right: 6px;

  &.hospital {
    background-color: #e6f7ff;
    color: #1890ff;
  }

  &.manufacturer {
    background-color: #fff7e6;
    color: #fa8c16;
  }
}

.company-cno {
  color: #999;
  font-size: 12px;
  margin-left: 4px;
}
</style>
